import { useEffect, useMemo, useState } from 'react';

import useSWR from 'swr';

import { LifeSpanService } from '@/components/composite/Charts/composed/MediaLifeSpan/services/LifeSpanService';

import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/src/useClickhouseMediaAnalytics';
import { useMediasV2 } from '@/utils/hooks/swr/useMediasV2';

import { get } from '@/utils/client';

import type { MediaType } from '@/types/media/mediaType';
import type { SocialProfileType } from '@/types/socialProfileType';

import type { LifeSpanChartData, LifeSpanData, PeriodData } from '@/lib/analytics/charts/type';
import { MEDIA_ANALYTICS_V2 } from '@/lib/routes/apiEndpoints';

type UseMediaLifeSpanDataParams = {
    socialProfile?: SocialProfileType | null;
    periodData?: PeriodData | null;
    activeFilter?: string;
    defaultFilter?: string;
};

type MediaAnalyticsResponse = {
    lifespan?: LifeSpanData;
    media?: MediaType;
    previous_media?: MediaType;
    next_media?: MediaType;
};

type UseMediaLifeSpanDataReturn = {
    isActive: boolean;
    isLoading: boolean;
    error?: Error;
    chartData?: LifeSpanChartData;
    medias: MediaType[];
    media?: MediaType;
    previousMedia?: MediaType;
    nextMedia?: MediaType;
    handleMediaChange: (mediaId: string | number) => void;
};

const useMediaLifeSpanData = ({
    socialProfile,
    periodData,
    activeFilter,
    defaultFilter = 'likes'
}: UseMediaLifeSpanDataParams): UseMediaLifeSpanDataReturn => {
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const [currentMediaId, setCurrentMediaId] = useState<string | null>(null);

    const network = socialProfile?.social_network_type;
    const isActive = isActiveForNetwork(network);

    // Memoize params to prevent unnecessary re-renders and ensure stable reference for SWR
    const mediasParams = useMemo(
        () =>
            isActive && socialProfile?.id && periodData?.periodStart && periodData?.periodEnd
                ? {
                      social_profile_ids: [socialProfile.id],
                      start: periodData.periodStart,
                      end: periodData.periodEnd
                  }
                : null,
        [isActive, socialProfile?.id, periodData?.periodStart, periodData?.periodEnd]
    );

    // Fetch medias list for the selected period
    const {
        dataMedias,
        mediasError,
        isLoadingInitialData: isLoadingMedias
    } = useMediasV2({
        params: mediasParams,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    // Flatten paginated medias into a single array
    const allMedias = useMemo(
        () => (dataMedias?.flatMap((page) => page.medias ?? []) ?? []) as MediaType[],
        [dataMedias]
    );

    // Build analytics endpoint URL for a specific media
    const buildAnalyticsUrl = (mediaId: string): string | null => {
        if (!mediaId || !socialProfile?.id) return null;
        return MEDIA_ANALYTICS_V2.replace('%SPID%', socialProfile.id.toString()).replace(
            '%mediaID%',
            mediaId.toString()
        );
    };

    // Memoize analytics params to prevent SWR cache staleness when periodData changes
    const analyticsParams = useMemo(() => {
        if (!periodData?.periodStart || !periodData?.periodEnd) {
            return null;
        }
        return { with_lifespan: true };
    }, [periodData?.periodStart, periodData?.periodEnd]);

    // Fetch analytics and lifespan data for the current selected media
    const {
        data: currentData,
        error: currentError,
        isLoading: isLoadingCurrent
    } = useSWR<MediaAnalyticsResponse, Error>(
        isActive && currentMediaId && analyticsParams
            ? [buildAnalyticsUrl(currentMediaId), analyticsParams]
            : null,
        ([url, params]: [string, Record<string, unknown>]) => {
            return get(url, params).then((res) => res.data);
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    // Fetch analytics and lifespan data for the previous media (for comparison)
    const previousMediaId = LifeSpanService.getPreviousMediaId(allMedias, currentMediaId);
    const {
        data: previousData,
        error: previousError,
        isLoading: isLoadingPrevious
    } = useSWR<MediaAnalyticsResponse, Error>(
        isActive && previousMediaId && analyticsParams
            ? [buildAnalyticsUrl(previousMediaId), analyticsParams]
            : null,
        ([url, params]: [string, Record<string, unknown>]) => {
            return get(url, params).then((res) => res.data);
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    // Synchronize currentMediaId with available medias
    useEffect(() => {
        if (!isActive) return;

        if (dataMedias && dataMedias.length > 0) {
            // Set first media as current when none is selected
            const firstMedia = dataMedias[0]?.medias?.[0] as { id: string };
            if (firstMedia?.id && !currentMediaId) {
                setCurrentMediaId(firstMedia.id);
            }
            // Reset currentMediaId if previously selected media is no longer available
            if (currentMediaId && !allMedias.some((m) => m.id === currentMediaId)) {
                setCurrentMediaId(null);
            }
        } else if (dataMedias?.length === 0) {
            // Clear selection when no medias are available for the period
            setCurrentMediaId(null);
        }
    }, [dataMedias, currentMediaId, isActive, allMedias]);

    const handleMediaChange = (newMediaId: string): void => {
        if (isActive) {
            setCurrentMediaId(newMediaId);
        }
    };

    // Return inactive state early if feature is not enabled for this network
    if (!isActive) {
        return {
            isActive: false,
            isLoading: false,
            medias: [],
            handleMediaChange: () => {}
        };
    }

    // Aggregate loading states from all data sources
    const isLoading = isLoadingMedias || isLoadingCurrent || isLoadingPrevious;

    // Extract navigation medias (previous, current, next) for carousel
    const navigationMedias = LifeSpanService.getMediasData(allMedias, currentMediaId);

    // Transform API response into chart-compatible format
    const getChartData = (): LifeSpanChartData | undefined => {
        // No chart data when medias list is empty
        if (!allMedias || allMedias.length === 0) {
            return undefined;
        }

        // No chart data when current media has no lifespan data
        if (!currentData?.lifespan) {
            return undefined;
        }

        // Merge current and previous media lifespan data for comparison
        const metricKey = activeFilter || defaultFilter;
        const graphData = LifeSpanService.merge(
            currentData.lifespan,
            previousData?.lifespan,
            metricKey
        );

        return {
            graphData,
            media: navigationMedias.media,
            previous_media: navigationMedias.previous_media,
            next_media: navigationMedias.next_media
        };
    };

    const chartData = getChartData();

    return {
        isActive: true,
        isLoading,
        error: mediasError || currentError || previousError,
        chartData,
        medias: allMedias,
        media: navigationMedias.media,
        previousMedia: navigationMedias.previous_media,
        nextMedia: navigationMedias.next_media,
        handleMediaChange
    };
};

export default useMediaLifeSpanData;
